import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, BookOpen, CalendarDays, Check, ChevronDown, Clock3, Download, ExternalLink, Eye,
  FileText, Image, Phone, RefreshCw, Search, ShieldCheck, Star, Wrench
} from 'lucide-react';
import resourcesService from '../../services/resourcesService';
import RegistrationRequestForm, { registrationRequestForms } from './RegistrationRequestForm';
import './AgentResources.css';

const initialsFor = (article) => article.short_name || article.title?.split(/\s+/).slice(0, 3).map(part => part[0]).join('').toUpperCase() || 'AR';
const dateLabel = value => value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not reviewed';
const relativeDate = value => {
  if (!value) return 'Recently';
  const days = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 86400000));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 14) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
};

const LoadingState = ({ detail = false }) => (
  <div className={`ar-loading ${detail ? 'detail' : ''}`} aria-label="Loading Agent Resources">
    <div className="ar-skeleton wide" />
    <div className="ar-skeleton medium" />
    <div className="ar-skeleton-grid">
      {[1, 2, 3, 4, 5, 6].map(item => <div className="ar-skeleton card" key={item} />)}
    </div>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="ar-empty-state">
    <div className="ar-empty-icon"><RefreshCw size={22} /></div>
    <h2>We couldn’t load Agent Resources</h2>
    <p>{message}</p>
    <button type="button" className="ar-button primary" onClick={onRetry}><RefreshCw size={15} /> Try again</button>
  </div>
);

const CategoryBadge = ({ children, slug = '' }) => <span className={`ar-category-badge cat-${slug}`}>{children || 'Resource'}</span>;

const imageCache = new Map();
const loadThumb = (id, variant) => {
  const key = `${variant}:${id}`;
  if (!imageCache.has(key)) {
    const promise = resourcesService.downloadMedia(id, false, { thumb: variant }).then(blob => new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const image = new window.Image();
      image.onload = () => resolve({ url, ratio: image.naturalWidth / image.naturalHeight || 1 });
      image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image could not be read')); };
      image.src = url;
    }));
    promise.catch(() => imageCache.delete(key));
    imageCache.set(key, promise);
  }
  return imageCache.get(key);
};

const useThumb = (mediaId, variant) => {
  const [image, setImage] = useState(null);
  useEffect(() => {
    let active = true;
    setImage(null);
    if (mediaId) loadThumb(mediaId, variant).then(result => { if (active) setImage(result); }).catch(() => {});
    return () => { active = false; };
  }, [mediaId, variant]);
  return image;
};

const LogoMark = ({ mediaId, height, maxWidth, fallback }) => {
  const logo = useThumb(mediaId, 1);
  if (!logo) return fallback;
  const width = Math.round(Math.min(maxWidth, Math.max(height, (height - 14) * logo.ratio + 14)));
  return <span className="ar-logo-plate" style={{ width, height }}><img src={logo.url} alt="" /></span>;
};

const IconMark = ({ mediaId, className, fallback }) => {
  const icon = useThumb(mediaId, 'icon');
  if (!icon) return fallback;
  return <div className={`${className} has-icon`}><img src={icon.url} alt="" /></div>;
};

const BrandMark = ({ article, className, fallback, logoHeight, logoMaxWidth, logoItem }) => {
  const iconId = article.icon_media_id || article.media_items?.find(item => item.usage_type === 'icon' && item.media_type === 'image')?.id;
  const logoId = logoItem?.id || article.logo_media_id;
  return (
    <IconMark mediaId={iconId} className={className} fallback={<LogoMark mediaId={logoId} height={logoHeight} maxWidth={logoMaxWidth} fallback={fallback} />} />
  );
};

const FooterLogo = ({ mediaId }) => {
  const logo = useThumb(mediaId, 1);
  return logo ? <div className="ar-footer-logo"><img src={logo.url} alt="" /></div> : null;
};

const ResourceCard = ({ article, featured = false }) => {
  const tabbed = article.page_type === 'tabbed';
  const color = article.brand_color || (tabbed ? 'var(--primary)' : 'var(--secondary)');
  return (
    <Link to={`/agent/resources/${article.slug}`} className={`ar-card ${tabbed ? 'profile' : 'article'} ${featured ? 'featured' : ''}`} style={{ '--resource-color': color }}>
      <div className="ar-card-accent" />
      <div className="ar-card-top">
        {tabbed ? <BrandMark article={article} className="ar-brand-mark" logoHeight={48} logoMaxWidth={140} fallback={<div className="ar-brand-mark" style={{ backgroundColor: color }}>{article.logo_url ? <img src={article.logo_url} alt="" /> : initialsFor(article)}</div>} /> : null}
        <div className="ar-card-heading">
          <div className="ar-card-title-row"><h3>{article.title}</h3>{article.is_featured && <Star size={14} fill="currentColor" />}</div>
          <CategoryBadge slug={article.category_slug}>{article.category_name}</CategoryBadge>
        </div>
      </div>
      <p className="ar-card-summary">{article.tagline || article.excerpt || 'Open this resource to learn more.'}</p>
      {!!article.tags?.length && <div className="ar-tags">{article.tags.slice(0, 3).map(tag => <span key={tag}>{tag}</span>)}{article.tags.length > 3 && <span>+{article.tags.length - 3}</span>}</div>}
      <div className="ar-card-footer"><span>{relativeDate(article.updated_at)}</span><strong>{tabbed ? 'View' : 'Read'} <ArrowRight size={13} /></strong></div>
    </Link>
  );
};

const RecentResource = ({ article }) => (
  <Link className="ar-recent-card" to={`/agent/resources/${article.slug}`}>
    {article.page_type === 'tabbed' ? <span className="ar-recent-initials" style={{ backgroundColor: article.brand_color || 'var(--primary)' }}>{initialsFor(article)}</span> : <span className="ar-recent-file"><FileText size={15} /></span>}
    <span><strong>{article.title}</strong><small>{relativeDate(article.updated_at)}</small></span>
  </Link>
);

const SecureMediaImage = ({ item }) => {
  const [source, setSource] = useState('');
  useEffect(() => {
    let objectUrl = '';
    let cancelled = false;
    resourcesService.downloadMedia(item.id).then(blob => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setSource(objectUrl);
    }).catch(() => { if (!cancelled) setSource(''); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [item.id]);
  return source ? <figure><img src={source} alt={item.alt_text || item.original_name} />{item.description && <figcaption>{item.description}</figcaption>}</figure> : <div className="ar-media-loading">Loading image…</div>;
};

const ResourceAttachments = ({ items: allItems }) => {
  const items = allItems?.filter(item => !['logo', 'icon'].includes(item.usage_type));
  if (!items?.length) return null;
  const inlineImages = items.filter(item => item.media_type === 'image' && ['inline', 'logo'].includes(item.usage_type));
  const downloads = [...items.filter(item => !inlineImages.includes(item)).reduce((unique, item) => {
    const key = `${item.original_name}|${item.file_size}`;
    if (!unique.has(key) || (item.link_text && !unique.get(key).link_text)) unique.set(key, item);
    return unique;
  }, new Map()).values()];
  const open = async item => {
    const blob = await resourcesService.downloadMedia(item.id, item.media_type === 'document');
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = item.original_name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  return (
    <section className="ar-attachments">
      {!!downloads.length && <h2>Downloads & Media</h2>}
      {!!inlineImages.length && <div className="ar-inline-media">{inlineImages.map(item => <SecureMediaImage key={`${item.id}-${item.usage_type}`} item={item} />)}</div>}
      {!!downloads.length && <div className="ar-downloads">{downloads.map(item => <button type="button" key={`${item.id}-${item.usage_type}`} onClick={() => open(item)}><span>{item.media_type === 'image' ? <Image size={18} /> : <FileText size={18} />}<strong>{item.link_text || item.original_name}</strong></span><Download size={16} /></button>)}</div>}
    </section>
  );
};

const FeedPosts = ({ feed }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setData(null);
    setError('');
    resourcesService.getFeed(feed).then(result => { if (active) setData(result); }).catch(failure => { if (active) setError(failure.response?.data?.error || 'Unable to load posts.'); });
    return () => { active = false; };
  }, [feed]);
  if (error) return <div className="ar-feed-note" role="alert">{error}</div>;
  if (!data) return <div className="ar-feed-note">Loading posts…</div>;
  if (!data.latest.length) return <div className="ar-feed-note">No posts have been published yet.</div>;
  return (
    <div className="ar-feed">
      {data.latest.map(post => (
        <article className="ar-post" key={post.id}>
          <header className="ar-post-header"><h2>{post.title}</h2><span><CalendarDays size={14} /> {dateLabel(post.published_at)}</span></header>
          <StandardArticle article={post} />
          <ResourceAttachments items={post.media_items} />
        </article>
      ))}
      {!!data.older.length && (
        <details className="ar-feed-older">
          <summary>Older posts ({data.older.length})</summary>
          <ul>{data.older.map(post => <li key={post.id}><Link to={`/agent/resources/${post.slug}`}>{post.title}</Link><span>{dateLabel(post.published_at)}</span></li>)}</ul>
        </details>
      )}
    </div>
  );
};

const RelatedResources = ({ resources }) => {
  if (!resources?.length) return null;
  return (
    <section className="ar-related">
      <div className="ar-section-heading"><h2>Related Resources</h2><p>Continue exploring helpful information.</p></div>
      <div className="ar-related-grid">{resources.map(resource => <ResourceCard key={resource.id} article={resource} />)}</div>
    </section>
  );
};

const StandardArticle = ({ article }) => (
  <div className="ar-article-body" dangerouslySetInnerHTML={{ __html: article.content }} />
);

const SectionSelect = ({ tabs, active, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const buttonRef = useRef(null);
  const activeIndex = Math.max(0, tabs.findIndex(tab => tab.slug === active.slug));
  const baseId = `ar-section-${active.id}`;

  const close = (returnFocus = true) => { setOpen(false); if (returnFocus) buttonRef.current?.focus(); };
  const openList = () => { setHighlight(activeIndex); setOpen(true); };
  const choose = tab => { onSelect(tab.slug); close(); };

  useEffect(() => {
    if (!open) return undefined;
    listRef.current?.focus();
    const onPointerDown = event => { if (!wrapRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const onListKeyDown = event => {
    const moves = { ArrowDown: (highlight + 1) % tabs.length, ArrowUp: (highlight - 1 + tabs.length) % tabs.length, Home: 0, End: tabs.length - 1 };
    if (event.key in moves) { event.preventDefault(); setHighlight(moves[event.key]); }
    else if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choose(tabs[highlight]); }
    else if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); }
    else if (event.key === 'Tab') setOpen(false);
  };

  return (
    <div className="ar-section-select" ref={wrapRef}>
      <span className="ar-section-label" id={`${baseId}-label`}>Section</span>
      <button
        type="button"
        ref={buttonRef}
        id={`${baseId}-button`}
        className="ar-section-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${baseId}-label ${baseId}-button`}
        onClick={() => (open ? close(false) : openList())}
        onKeyDown={event => { if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); openList(); } }}
      >
        <span>{active.title}</span><ChevronDown size={20} aria-hidden="true" />
      </button>
      {open && (
        <ul id={`${baseId}-list`} ref={listRef} className="ar-section-list" role="listbox" tabIndex={-1} aria-labelledby={`${baseId}-label`} aria-activedescendant={`${baseId}-option-${highlight}`} onKeyDown={onListKeyDown}>
          {tabs.map((tab, index) => {
            const selected = tab.slug === active.slug;
            return (
              <li key={tab.id} id={`${baseId}-option-${index}`} role="option" aria-selected={selected} className={`${selected ? 'selected' : ''} ${index === highlight ? 'highlight' : ''}`.trim()} onPointerMove={() => highlight !== index && setHighlight(index)} onClick={() => choose(tab)}>
                <span>{tab.title}</span>{selected && <Check size={18} aria-hidden="true" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const TabbedArticle = ({ article, preview }) => {
  const tabs = article.sections || [];
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const initialTab = tabs.find(tab => tab.slug === requestedTab)?.slug || tabs[0]?.slug || '';
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => setActiveTab(initialTab), [article.id, tabs, initialTab]);
  const active = tabs.find(tab => tab.slug === activeTab) || tabs[0];
  if (!tabs.length) return <StandardArticle article={article} />;

  const selectTab = slug => {
    setActiveTab(slug);
    if (preview) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', slug);
    setSearchParams(next, { replace: true });
  };

  const handleKeyDown = (event, index) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let next = index;
    if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    selectTab(tabs[next].slug);
    event.currentTarget.parentElement.querySelectorAll('[role="tab"]')[next]?.focus();
  };

  return (
    <div className="ar-tab-layout">
      <SectionSelect tabs={tabs} active={active} onSelect={selectTab} />
      <div className="ar-tab-list" role="tablist" aria-label="Resource topics">
        {tabs.map((tab, index) => {
          const selected = tab.slug === active.slug;
          return (
            <button
              type="button"
              role="tab"
              key={tab.id}
              aria-selected={selected}
              aria-controls={`ar-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              className={selected ? 'active' : ''}
              onClick={() => selectTab(tab.slug)}
              onKeyDown={event => handleKeyDown(event, index)}
            >
              <span>{tab.title}</span>
            </button>
          );
        })}
      </div>
      <section id={`ar-panel-${active.id}`} role="tabpanel" className="ar-tab-panel" key={active.id}>
        <div dangerouslySetInnerHTML={{ __html: active.content }} />
        {Number(article.registration_request_enabled) === 1 && registrationRequestForms(article).filter(form => form.tab === active.slug).map(form => (
          <RegistrationRequestForm key={`${article.id}-${active.slug}-${form.key}`} article={article} form={form} preview={preview || article.status !== 'published'} />
        ))}
        <ResourceAttachments items={article.media_items?.filter(item => item.section_slug === active.slug)} />
      </section>
    </div>
  );
};

export const ResourceDetail = ({ article, onBack, canManage = false, preview = false, backLabel = 'Back to Agent Resources' }) => {
  const tabbed = article.page_type === 'tabbed';
  const logoItem = article.media_items?.find(item => item.usage_type === 'logo' && item.media_type === 'image');
  const hasIcon = Boolean(article.media_items?.some(item => item.usage_type === 'icon' && item.media_type === 'image'));
  return (
    <div className="ar-page ar-detail-page tw:font-voyager-body">
      <button type="button" className="ar-back" onClick={onBack}><ArrowLeft size={16} /> {backLabel}</button>
      <article className="ar-detail-card">
        <header className={`ar-detail-header ${tabbed ? 'profile' : 'article'}`}>
          {tabbed && (
            <BrandMark
              article={article}
              className="ar-detail-brand"
              logoItem={logoItem}
              logoHeight={72}
              logoMaxWidth={240}
              fallback={(
                <div className="ar-detail-brand" style={{ backgroundColor: article.brand_color || 'var(--primary)' }}>
                  {article.logo_url ? <img src={article.logo_url} alt={`${article.title} logo`} /> : initialsFor(article)}
                </div>
              )}
            />
          )}
          <div className="ar-detail-title">
            <div className="ar-detail-title-line"><h1>{article.title}</h1><CategoryBadge slug={article.category_slug}>{article.parent_category_name || article.category_name}</CategoryBadge>{article.parent_category_name && <span className="ar-subcategory-badge">{article.category_name}</span>}</div>
            {(article.tagline || (!tabbed && article.excerpt)) && <p>{article.tagline || article.excerpt}</p>}
            {!!article.tags?.length && <div className="ar-tags">{article.tags.map(tag => <span key={tag}>{tag}</span>)}</div>}
            <div className="ar-detail-meta">
              <span><CalendarDays size={14} /> {article.post_feed ? `Posted ${dateLabel(article.published_at || article.updated_at)}` : `Updated ${dateLabel(article.updated_at)}${article.updated_by_name ? ` by ${article.updated_by_name}` : ''}`}</span>
              {article.last_reviewed_at && <span><ShieldCheck size={14} /> Reviewed {dateLabel(article.last_reviewed_at)}</span>}
            </div>
          </div>
          {tabbed && (article.website_url || article.phone || canManage) && (
            <div className="ar-detail-actions">
              {article.phone && <a href={`tel:${article.phone.replace(/[^\d+]/g, '')}`}><Phone size={15} /> {article.phone}</a>}
              {article.website_url && <a href={article.website_url} target="_blank" rel="noopener noreferrer"><ExternalLink size={15} /> Website</a>}
              {canManage && <Link to={`/admin/resources/${article.id}/edit`}><Eye size={15} /> Edit Page</Link>}
            </div>
          )}
        </header>
        <div className="ar-detail-content">
          {tabbed ? <TabbedArticle article={article} preview={preview} /> : <StandardArticle article={article} />}
          {!tabbed && Number(article.registration_request_enabled) === 1 && registrationRequestForms(article).map(form => <RegistrationRequestForm key={`${article.id}-${form.key}`} article={article} form={form} preview={preview || article.status !== 'published'} />)}
          {article.feed_page && <FeedPosts feed={article.feed_page} />}
        </div>
        <ResourceAttachments items={article.media_items?.filter(item => !item.section_slug)} />
        {tabbed && hasIcon && logoItem && <FooterLogo mediaId={logoItem.id} />}
      </article>
      <RelatedResources resources={article.related_resources} />
    </div>
  );
};

const AgentResources = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [article, setArticle] = useState(null);
  const [meta, setMeta] = useState({ canManage: false });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const category = searchParams.get('category') || '';
  const query = searchParams.get('search') || '';

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const request = slug
      ? Promise.all([resourcesService.getArticle(slug), resourcesService.getMeta()])
      : Promise.all([resourcesService.getCategories(), resourcesService.getArticles({ category: category || undefined, search: query || undefined }), resourcesService.getMeta()]);
    request.then(data => {
      if (!active) return;
      if (slug) {
        setArticle(data[0]);
        setMeta(data[1]);
      } else {
        setCategories(data[0]);
        setArticles(data[1]);
        setMeta(data[2]);
      }
    }).catch(err => {
      if (active) setError(err.response?.data?.error || 'Unable to load Agent Resources.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [slug, category, query, reloadKey]);

  const submitSearch = event => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    search.trim() ? next.set('search', search.trim()) : next.delete('search');
    next.delete('category');
    setSearchParams(next);
  };

  const chipCategories = useMemo(() => {
    const visible = categories.filter(item => item.article_count > 0);
    const ids = new Set(visible.map(item => item.id));
    const roots = visible.filter(item => !item.parent_id || !ids.has(item.parent_id));
    return roots.flatMap(root => [root, ...visible.filter(item => item.parent_id === root.id)]);
  }, [categories]);
  const featured = useMemo(() => articles.filter(item => item.is_featured).slice(0, 4), [articles]);
  const recent = useMemo(() => [...articles].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)).slice(0, 5), [articles]);

  if (loading) return <div className="ar-page"><LoadingState detail={Boolean(slug)} /></div>;
  if (error) return <div className="ar-page"><ErrorState message={error} onRetry={() => setReloadKey(value => value + 1)} /></div>;
  if (slug && article) {
    const feedPost = article.post_feed;
    return <ResourceDetail article={article} canManage={meta.canPublish} onBack={() => navigate(feedPost ? `/agent/resources/${feedPost}` : '/agent/resources')} backLabel={feedPost ? 'Back to all posts' : undefined} />;
  }

  return (
    <div className="ar-page tw:font-voyager-body">
      <header className="ar-hero">
        <div><span className="ar-kicker">MainStreet Travel Agency</span><h1>Agent Resources</h1><p>Supplier information, agency policies, training, marketing resources, and essential documents—all in one place.</p></div>
        {meta.canManage && <Link className="ar-button secondary" to="/admin/resources"><Wrench size={15} /> Manage Resources</Link>}
      </header>
      <section className="ar-discovery-panel">
        <form className="ar-search" onSubmit={submitSearch}>
          <Search size={18} /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search resources, suppliers, destinations…" aria-label="Search Agent Resources" />
          {search && <button type="button" className="ar-clear" onClick={() => setSearch('')}>Clear</button>}
          <button type="submit" className="ar-button primary">Search</button>
        </form>
        <div className="ar-category-strip" aria-label="Resource categories">
          <button type="button" className={!category ? 'active' : ''} onClick={() => setSearchParams(query ? { search: query } : {})}>All Resources <span>{articles.length}</span></button>
          {chipCategories.map(item => <button type="button" key={item.id} className={category === item.slug ? 'active' : ''} onClick={() => setSearchParams({ ...(query ? { search: query } : {}), category: item.slug })}>{item.name} <span>{item.article_count}</span></button>)}
        </div>
      </section>
      {featured.length > 0 && !query && !category && <section className="ar-section ar-featured-section"><div className="ar-section-heading inline"><h2><Star size={14} fill="currentColor" /> Featured Resources</h2></div><div className="ar-grid featured">{featured.map(item => <ResourceCard featured key={item.id} article={item} />)}</div></section>}
      {recent.length > 0 && !query && !category && <section className="ar-section ar-recent-section"><div className="ar-section-heading inline"><h2><Clock3 size={15} /> Recently Updated</h2></div><div className="ar-recent-strip">{recent.map(item => <RecentResource key={item.id} article={item} />)}</div></section>}
      <section className="ar-section">
        <div className="ar-section-heading split"><h2>{query ? `Results for “${query}”` : category ? categories.find(item => item.slug === category)?.name || 'Resources' : 'All Resources'}</h2><p>{articles.length} resources</p></div>
        {articles.length ? <div className="ar-grid">{articles.map(item => <ResourceCard key={item.id} article={item} />)}</div> : <div className="ar-empty-state"><div className="ar-empty-icon"><Search size={22} /></div><h2>No resources found</h2><p>Try a different search or category.</p><button className="ar-button secondary" type="button" onClick={() => { setSearch(''); setSearchParams({}); }}>Clear filters</button></div>}
      </section>
    </div>
  );
};

export default AgentResources;
